import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Leaf,
  ArrowLeft,
  Plus,
  Edit,
  Trash2,
  LogOut,
} from "lucide-react";
import { ImageUploader } from "@/components/ImageUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const auth = sessionStorage.getItem("adminAuth");
  if (!auth) return null;
  return { Authorization: `Basic ${auth}` };
};

export const AdminBlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    image_url: "",
    author: "WEH International",
    is_featured: false,
  });

  const headers = getAuthHeaders();

  useEffect(() => {
    if (!headers) {
      navigate("/admin");
      return;
    }
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      toast.error("Failed to load blog posts");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const resetForm = () => {
    setForm({
      title: "",
      slug: "",
      excerpt: "",
      content: "",
      image_url: "",
      author: "WEH International",
      is_featured: false,
    });
    setEditingPost(null);
    setShowForm(false);
  };

  const handleEdit = (post) => {
    setForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      image_url: post.image_url,
      author: post.author,
      is_featured: post.is_featured,
    });
    setEditingPost(post);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.content) {
      toast.error("Please fill in title and content");
      return;
    }

    const postData = {
      ...form,
      slug: form.slug || generateSlug(form.title),
    };

    try {
      if (editingPost) {
        await axios.put(`${API}/admin/blog/${editingPost.id}`, postData, { headers });
        toast.success("Blog post updated!");
      } else {
        await axios.post(`${API}/admin/blog`, postData, { headers });
        toast.success("Blog post created!");
      }
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error("Failed to save blog post");
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      await axios.delete(`${API}/admin/blog/${postId}`, { headers });
      toast.success("Blog post deleted!");
      fetchPosts();
    } catch (error) {
      toast.error("Failed to delete blog post");
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-deep-moss font-serif text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone-cream animate-fade-in">
      {/* Header */}
      <header className="bg-deep-moss text-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Leaf className="w-6 h-6" />
              <span className="font-serif text-xl font-semibold">WEH Admin</span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 font-sans text-sm text-white/80 hover:text-white"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/admin/dashboard"
            data-testid="back-to-dashboard"
            className="text-stone-500 hover:text-deep-moss"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-serif text-3xl font-semibold text-stone-900">Blog Posts</h1>
        </div>

        {/* Add/Edit Form */}
        {showForm ? (
          <div className="bg-white p-6 border border-stone-200 mb-8 animate-fade-in">
            <h2 className="font-serif text-xl font-semibold text-stone-900 mb-6">
              {editingPost ? "Edit Blog Post" : "Create New Blog Post"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="blog-title-input"
                    className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                  />
                </div>
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-1">
                    Slug (auto-generated if empty)
                  </label>
                  <input
                    type="text"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    data-testid="blog-slug-input"
                    className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                    placeholder="leave-empty-to-auto-generate"
                  />
                </div>
              </div>
              <div>
                <label className="block font-sans text-sm text-stone-700 mb-1">Excerpt</label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  data-testid="blog-excerpt-input"
                  rows={2}
                  className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                  placeholder="Brief summary for preview..."
                />
              </div>
              <div>
                <label className="block font-sans text-sm text-stone-700 mb-1">Content *</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  data-testid="blog-content-input"
                  rows={10}
                  className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                  placeholder="Write your blog post content here..."
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-1">Featured Image</label>
                  <ImageUploader 
                    currentImage={form.image_url}
                    onUpload={(url) => setForm({ ...form, image_url: url })}
                  />
                </div>
                <div>
                  <label className="block font-sans text-sm text-stone-700 mb-1">Author</label>
                  <input
                    type="text"
                    value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    data-testid="blog-author-input"
                    className="w-full border border-stone-200 rounded px-3 py-2 font-sans text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 font-sans text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_featured}
                    onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                    className="rounded"
                  />
                  Featured Post
                </label>
              </div>
              <div className="flex gap-4 pt-4">
                <button
                  type="submit"
                  data-testid="save-blog-btn"
                  className="bg-deep-moss hover:bg-deep-moss/90 text-white rounded-full px-6 py-2 font-sans text-sm font-medium"
                >
                  {editingPost ? "Update Post" : "Publish Post"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  data-testid="cancel-btn"
                  className="border border-stone-300 text-stone-700 rounded-full px-6 py-2 font-sans text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        ) : (
          <button
            onClick={() => setShowForm(true)}
            data-testid="add-blog-btn"
            className="flex items-center gap-2 bg-deep-moss hover:bg-deep-moss/90 text-white rounded-full px-6 py-2 font-sans text-sm font-medium mb-8"
          >
            <Plus className="w-4 h-4" />
            Create New Post
          </button>
        )}

        {/* Posts List */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((post) => (
            <div
              key={post.id}
              data-testid={`blog-card-${post.id}`}
              className="bg-white border border-stone-200 overflow-hidden"
            >
              {post.image_url && (
                <img
                  src={post.image_url}
                  alt={post.title}
                  className="w-full h-40 object-cover"
                />
              )}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-serif text-lg font-medium text-stone-900 line-clamp-2">
                    {post.title}
                  </h3>
                  {post.is_featured && (
                    <span className="text-xs bg-terracotta/10 text-terracotta px-2 py-0.5 rounded flex-shrink-0 ml-2">
                      Featured
                    </span>
                  )}
                </div>
                <p className="font-sans text-sm text-stone-600 line-clamp-2 mb-4">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-sans text-xs text-stone-500">{post.author}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(post)}
                      data-testid={`edit-blog-${post.id}`}
                      className="p-2 text-stone-500 hover:text-deep-moss"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(post.id)}
                      data-testid={`delete-blog-${post.id}`}
                      className="p-2 text-stone-500 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
